import { useEffect, useState } from 'react';
import {
  addGrievanceActivity,
  assignGrievanceInvestigator,
  getGrievance,
  listEmployees,
  listGrievances,
  resolveGrievance,
  submitGrievance,
  type EmployeeGrievance,
  type EmployeeRecord,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';
import { StatusBadge } from '../components/StatusBadge';

export function Grievances() {
  const { session, can } = useAuth();
  const [tab, setTab] = useState<'all' | 'my' | 'detail'>('all');
  const [grievances, setGrievances] = useState<EmployeeGrievance[]>([]);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [selectedGrievance, setSelectedGrievance] = useState<EmployeeGrievance | null>(null);
  const [investigationActivities, setInvestigationActivities] = useState<any[]>([]);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Submit Modal
  const [submitModal, setSubmitModal] = useState(false);
  const [category, setCategory] = useState<'harassment' | 'posh' | 'compensation' | 'discrimination' | 'workplace_safety' | 'manager_conflict' | 'other'>('manager_conflict');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [againstEmployeeId, setAgainstEmployeeId] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');

  // Assign Investigator Modal
  const [assignModal, setAssignModal] = useState(false);
  const [selectedInvestigatorId, setSelectedInvestigatorId] = useState('');

  // Add Activity Modal
  const [activityModal, setActivityModal] = useState(false);
  const [actType, setActType] = useState<'interview' | 'hearing' | 'evidence_review' | 'case_note'>('interview');
  const [actSummary, setActSummary] = useState('');
  const [actDetails, setActDetails] = useState('');

  // Resolve Modal
  const [resolveModal, setResolveModal] = useState(false);
  const [resStatus, setResStatus] = useState<'resolved' | 'closed'>('resolved');
  const [resolutionSummary, setResolutionSummary] = useState('');

  async function loadData() {
    try {
      const [gRes, empsRes] = await Promise.all([
        listGrievances().catch(() => ({ data: [] })),
        listEmployees().catch(() => ({ items: [] })),
      ]);
      setGrievances(gRes.data || []);
      setEmployees(empsRes.items || []);

      if (empsRes.items?.length && !selectedInvestigatorId) {
        setSelectedInvestigatorId(empsRes.items[0].id);
      }
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load grievances' });
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  async function handleOpenDetail(g: EmployeeGrievance) {
    try {
      setSelectedGrievance(g);
      const res = await getGrievance(g.id);
      setSelectedGrievance(res.data.grievance);
      setInvestigationActivities(res.data.activities || []);
      setTab('detail');
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load case details' });
    }
  }

  async function handleSubmitGrievance(e: React.FormEvent) {
    e.preventDefault();
    try {
      await submitGrievance({
        category,
        subject,
        description,
        againstEmployeeId: againstEmployeeId || null,
        isAnonymous,
        severity,
      });
      setMsg({
        type: 'success',
        text: 'Grievance lodged securely. Anti-retaliation protection is active (shielded from direct managers).',
      });
      setSubmitModal(false);
      setSubject('');
      setDescription('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit grievance' });
    }
  }

  async function handleAssignInvestigator(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedGrievance || !selectedInvestigatorId) return;
    try {
      await assignGrievanceInvestigator(selectedGrievance.id, selectedInvestigatorId);
      setMsg({ type: 'success', text: 'Investigator assigned to confidential case.' });
      setAssignModal(false);
      await handleOpenDetail(selectedGrievance);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to assign investigator' });
    }
  }

  async function handleAddActivity(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedGrievance) return;
    try {
      await addGrievanceActivity(selectedGrievance.id, {
        activityType: actType,
        summary: actSummary,
        confidentialNotes: actDetails || null,
      });
      setMsg({ type: 'success', text: 'Investigation note/hearing recorded.' });
      setActivityModal(false);
      setActSummary('');
      setActDetails('');
      await handleOpenDetail(selectedGrievance);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to record activity' });
    }
  }

  async function handleResolveGrievance(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedGrievance) return;
    try {
      await resolveGrievance(selectedGrievance.id, {
        status: resStatus,
        resolutionSummary,
      });
      setMsg({ type: 'success', text: `Case #${selectedGrievance.grievance_number} officially closed as ${resStatus.toUpperCase()}.` });
      setResolveModal(false);
      setResolutionSummary('');
      await handleOpenDetail(selectedGrievance);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to resolve grievance' });
    }
  }

  const isOfficer = can('grievance.manage');
  const myGrievances = grievances.filter((g) => g.complainant_employee_id === session?.user.employeeId);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Integrity, Ethics & Employee Relations</div>
          <h1>Confidential Grievance & Dispute Resolution Centre</h1>
        </div>
        <div className="toolbar">
          <button className="btn" onClick={() => setSubmitModal(true)}>
            <Icon name="lock" /> File Confidential Grievance
          </button>
        </div>
      </div>

      {msg && (
        <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <Icon name={msg.type === 'success' ? 'check' : 'alertCircle'} />
          <span>{msg.text}</span>
          <button className="btn-quiet" style={{ marginLeft: 'auto' }} onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {/* Security Banner */}
      <div
        style={{
          background: 'rgba(59, 130, 246, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.25)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Icon name="shieldCheck" size="lg" color="#3b82f6" />
        <div style={{ fontSize: 13, color: '#93c5fd' }}>
          <strong>Anti-Retaliation Protocol Active:</strong> Grievance cases are strictly confidential and are isolated from immediate reporting managers by default. Only authorized Ethics / POSH Officers and appointed investigators hold access permissions.
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {isOfficer && (
          <button className={`tab-btn ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>
            <Icon name="departments" /> All Enterprise Cases ({grievances.length})
          </button>
        )}
        <button className={`tab-btn ${tab === 'my' ? 'active' : ''}`} onClick={() => setTab('my')}>
          <Icon name="employees" /> My Submitted Cases ({myGrievances.length})
        </button>
        {selectedGrievance && (
          <button className={`tab-btn ${tab === 'detail' ? 'active' : ''}`} onClick={() => setTab('detail')}>
            <Icon name="database" /> Case #{selectedGrievance.grievance_number} Detail
          </button>
        )}
      </div>

      {/* TAB 1: ALL CASES (OFFICER VIEW) */}
      {tab === 'all' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Active & Historical Grievance Ledger</h2>
            <span className="muted" style={{ fontSize: 13 }}>{grievances.length} Registered Matters</span>
          </div>
          {grievances.length === 0 ? (
            <div className="empty">No grievances logged in the system.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>Category & Severity</th>
                  <th>Subject</th>
                  <th>Complainant</th>
                  <th>Accused Party</th>
                  <th>Investigator</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {grievances.map((g) => (
                  <tr key={g.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{g.grievance_number}</td>
                    <td>
                      <div style={{ textTransform: 'capitalize', fontWeight: 600 }}>{g.category.replace('_', ' ')}</div>
                      <span
                        className="pill"
                        style={{
                          fontSize: 11,
                          marginTop: 4,
                          background:
                            g.severity === 'critical' ? '#ef4444' : g.severity === 'high' ? '#f97316' : '#64748b',
                          color: '#fff',
                        }}
                      >
                        {g.severity.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{g.subject}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{new Date(g.created_at).toLocaleDateString()}</div>
                    </td>
                    <td>
                      {g.is_anonymous ? (
                        <span className="pill" style={{ background: '#334155', color: '#cbd5e1' }}>🔒 Anonymous</span>
                      ) : (
                        <div>{g.complainant_first_name} {g.complainant_last_name}</div>
                      )}
                    </td>
                    <td>{g.against_first_name ? `${g.against_first_name} ${g.against_last_name}` : '—'}</td>
                    <td>{g.investigator_first_name ? `${g.investigator_first_name} ${g.investigator_last_name}` : <span className="muted">Unassigned</span>}</td>
                    <td><StatusBadge status={g.status} /></td>
                    <td>
                      <button className="btn-quiet" onClick={() => handleOpenDetail(g)}>
                        View Investigation &rarr;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 2: MY CASES */}
      {tab === 'my' && (
        <div className="panel">
          <div className="panel-head">
            <h2>My Grievances & Appeals</h2>
            <span className="muted" style={{ fontSize: 13 }}>Private tracking</span>
          </div>
          {myGrievances.length === 0 ? (
            <div className="empty">You have not submitted any grievances.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>Category</th>
                  <th>Subject</th>
                  <th>Date Logged</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {myGrievances.map((g) => (
                  <tr key={g.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{g.grievance_number}</td>
                    <td style={{ textTransform: 'capitalize' }}>{g.category.replace('_', ' ')}</td>
                    <td style={{ fontWeight: 600 }}>{g.subject}</td>
                    <td>{new Date(g.created_at).toLocaleDateString()}</td>
                    <td><StatusBadge status={g.status} /></td>
                    <td>
                      <button className="btn-quiet" onClick={() => handleOpenDetail(g)}>
                        Track Case &rarr;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 3: CASE DETAIL & INVESTIGATION LOG */}
      {tab === 'detail' && selectedGrievance && (
        <div>
          <div className="detail-grid" style={{ marginBottom: 24 }}>
            <div className="panel">
              <div className="panel-head">
                <div>
                  <div className="mono muted" style={{ fontSize: 12 }}>Case Identifier: {selectedGrievance.grievance_number}</div>
                  <h2>{selectedGrievance.subject}</h2>
                </div>
                <StatusBadge status={selectedGrievance.status} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Category & Severity</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="pill">{selectedGrievance.category.replace('_', ' ').toUpperCase()}</span>
                  <span className="pill" style={{ background: '#3b82f6', color: '#fff' }}>Severity: {selectedGrievance.severity.toUpperCase()}</span>
                  {selectedGrievance.is_anonymous ? (
                    <span className="pill" style={{ background: '#475569', color: '#fff' }}>🔒 Anonymous Complainant</span>
                  ) : (
                    <span className="pill" style={{ background: '#1e293b' }}>
                      Complainant: {selectedGrievance.complainant_first_name} {selectedGrievance.complainant_last_name}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Case Description & Statement</label>
                <div
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 6,
                    padding: 12,
                    fontSize: 14,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selectedGrievance.description}
                </div>
              </div>

              {selectedGrievance.status === 'resolved' && (
                <div
                  style={{
                    background: 'rgba(34, 197, 94, 0.08)',
                    border: '1px solid rgba(34, 197, 94, 0.25)',
                    borderRadius: 6,
                    padding: 14,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#4ade80', marginBottom: 4 }}>Official Resolution Sign-off</div>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>{selectedGrievance.resolution_summary}</div>
                </div>
              )}

              {isOfficer && selectedGrievance.status !== 'resolved' && (
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button className="btn-quiet" onClick={() => setAssignModal(true)}>
                    <Icon name="employees" /> {selectedGrievance.investigator_employee_id ? 'Reassign Investigator' : 'Assign Investigator'}
                  </button>
                  <button className="btn-quiet" onClick={() => setActivityModal(true)}>
                    <Icon name="plus" /> Add Hearing / Investigation Note
                  </button>
                  <button className="btn" style={{ background: '#22c55e' }} onClick={() => setResolveModal(true)}>
                    <Icon name="check" /> Conclude & Resolve Case
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar Meta */}
            <div className="panel">
              <div className="panel-head">
                <h2>Investigation Team & Status</h2>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div className="muted" style={{ fontSize: 12 }}>Lead Investigator</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {selectedGrievance.investigator_first_name
                      ? `${selectedGrievance.investigator_first_name} ${selectedGrievance.investigator_last_name}`
                      : 'Not Assigned Yet'}
                  </div>
                </div>

                <div>
                  <div className="muted" style={{ fontSize: 12 }}>Respondent / Accused Party</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {selectedGrievance.against_first_name
                      ? `${selectedGrievance.against_first_name} ${selectedGrievance.against_last_name}`
                      : 'None Specified / Systemic Issue'}
                  </div>
                </div>

                <div>
                  <div className="muted" style={{ fontSize: 12 }}>Lodged On</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {new Date(selectedGrievance.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Investigation Timeline */}
          <div className="panel">
            <div className="panel-head">
              <h2>Confidential Case Activities & Hearings ({investigationActivities.length})</h2>
              {isOfficer && (
                <button className="btn-quiet" onClick={() => setActivityModal(true)}>
                  + Record Activity
                </button>
              )}
            </div>

            {investigationActivities.length === 0 ? (
              <div className="empty">No investigation activities or hearings logged yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {investigationActivities.map((act) => (
                  <div
                    key={act.id}
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 6,
                      padding: 14,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span className="pill" style={{ textTransform: 'uppercase', fontSize: 11 }}>
                          {act.activity_type.replace('_', ' ')}
                        </span>
                        <strong style={{ fontSize: 14 }}>{act.summary}</strong>
                      </div>
                      <span className="muted" style={{ fontSize: 12 }}>
                        {new Date(act.created_at || act.activity_date).toLocaleString()}
                      </span>
                    </div>
                    {act.confidential_notes && (
                      <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 6, whiteSpace: 'pre-wrap' }}>
                        {act.confidential_notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 1: FILE GRIEVANCE */}
      {submitModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-head">
              <h2>Lodge Confidential Grievance</h2>
              <button className="btn-quiet" onClick={() => setSubmitModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitGrievance}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Category</label>
                  <select
                    className="input"
                    value={category}
                    onChange={(e: any) => setCategory(e.target.value)}
                    required
                  >
                    <option value="manager_conflict">Manager / Reporting Conflict</option>
                    <option value="posh">POSH (Prevention of Sexual Harassment)</option>
                    <option value="harassment">Harassment / Bullying</option>
                    <option value="discrimination">Bias / Discrimination</option>
                    <option value="compensation">Compensation / Payroll Dispute</option>
                    <option value="workplace_safety">Workplace Safety / Health</option>
                    <option value="other">General Grievance</option>
                  </select>
                </div>
                <div>
                  <label className="label">Severity Level</label>
                  <select
                    className="input"
                    value={severity}
                    onChange={(e: any) => setSeverity(e.target.value)}
                  >
                    <option value="low">Low (Advisory)</option>
                    <option value="medium">Medium (Standard Review)</option>
                    <option value="high">High (Urgent Attention)</option>
                    <option value="critical">Critical (Immediate Escalation)</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Subject Line</label>
                <input
                  className="input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  placeholder="Concise summary of the grievance..."
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Person Against Whom Grievance is Filed (Optional)</label>
                <select
                  className="input"
                  value={againstEmployeeId}
                  onChange={(e) => setAgainstEmployeeId(e.target.value)}
                >
                  <option value="">No Individual Specified (Organizational / Policy)</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} — {emp.designation}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Detailed Statement & Circumstances</label>
                <textarea
                  className="input"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  placeholder="Please state dates, events, witnesses, and specific details..."
                />
              </div>

              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  id="anonCheck"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
                <label htmlFor="anonCheck" style={{ fontSize: 13, cursor: 'pointer' }}>
                  <strong>Submit Anonymously:</strong> Hide my identity from all case logs and investigators.
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setSubmitModal(false)}>Cancel</button>
                <button type="submit" className="btn">Submit Grievance Securely</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: ASSIGN INVESTIGATOR */}
      {assignModal && selectedGrievance && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-head">
              <h2>Assign Lead Investigator</h2>
              <button className="btn-quiet" onClick={() => setAssignModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAssignInvestigator}>
              <div style={{ marginBottom: 16 }}>
                <label className="label">Select Officer / Investigator</label>
                <select
                  className="input"
                  value={selectedInvestigatorId}
                  onChange={(e) => setSelectedInvestigatorId(e.target.value)}
                  required
                >
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} — {emp.designation}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setAssignModal(false)}>Cancel</button>
                <button type="submit" className="btn">Assign Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: ADD INVESTIGATION ACTIVITY */}
      {activityModal && selectedGrievance && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-head">
              <h2>Record Hearing / Investigation Entry</h2>
              <button className="btn-quiet" onClick={() => setActivityModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddActivity}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Activity Type</label>
                <select
                  className="input"
                  value={actType}
                  onChange={(e: any) => setActType(e.target.value)}
                >
                  <option value="interview">Witness / Party Interview</option>
                  <option value="hearing">Formal Hearing Committee</option>
                  <option value="evidence_review">Evidence & Document Review</option>
                  <option value="case_note">Confidential Case Note</option>
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Activity Summary</label>
                <input
                  className="input"
                  value={actSummary}
                  onChange={(e) => setActSummary(e.target.value)}
                  required
                  placeholder="e.g. Complainant testimony session"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Detailed Notes / Statements</label>
                <textarea
                  className="input"
                  rows={3}
                  value={actDetails}
                  onChange={(e) => setActDetails(e.target.value)}
                  placeholder="Key testimonies, timestamps, and findings..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setActivityModal(false)}>Cancel</button>
                <button type="submit" className="btn">Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: CONCLUDE CASE */}
      {resolveModal && selectedGrievance && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 540 }}>
            <div className="modal-head">
              <h2>Official Case Resolution</h2>
              <button className="btn-quiet" onClick={() => setResolveModal(false)}>✕</button>
            </div>
            <form onSubmit={handleResolveGrievance}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Resolution Status</label>
                <select
                  className="input"
                  value={resStatus}
                  onChange={(e: any) => setResStatus(e.target.value)}
                >
                  <option value="resolved">Resolved / Action Enacted</option>
                  <option value="closed">Closed / Inconclusive</option>
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Resolution Summary</label>
                <textarea
                  className="input"
                  rows={3}
                  value={resolutionSummary}
                  onChange={(e) => setResolutionSummary(e.target.value)}
                  required
                  placeholder="Official finding of the investigation committee..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setResolveModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ background: '#22c55e' }}>Confirm & Close Case</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
